/* eslint-disable react/prop-types */
import { h, render, Fragment } from 'preact'
import { useDispatch } from 'react-redux'
import { useEffect, useState } from 'preact/hooks'

import { requestAddItemToCart, requestDeleteItemFromCart } from '@state/cart/cartSlice'

import { requestAddItemToWishlist } from '@state/wishlist/wishlistSlice'
import { requestAddItemToCompareList } from '@state/comparelist/compareProductsSlice'

import { productTypeFlags, modelTypes } from 'app/constants'
import { keygen, iterable, redirect } from '@utils/misc'
import useSSR from '@core/hooks/useSSR'
import translate from '@core/translate'

import getProductRibbons from '@features/productRibbons'
import Toaster from '@features/toaster/Toast'

import AddedToCart from '@components/addedToCart'
import ProductBox from '@ui/ProductBox/ProductBox'

export default function ProductList({
	products,
	title,
	className,
	footer,
	scrollable,
	classNames = {
		listContainer: 'featured-list-container',
		listTitle: 'featured-list-title',
		listWrapper: 'featured-list-wrapper',
	},
}) {
	const dispatch = useDispatch()
	const [productData, setProductData] = useState(products)
	const { catalogSettings: settings } = useSSR([modelTypes.catalogSettings])
	const { compareProductsEnabled } = settings

	useEffect(() => {
		if (productData !== products) {
			getProductRibbons()

			return () => setProductData(products)
		}
	}, [products, productData])

	function onAddToCartClick(product) {
		if (
			product.productTypeId == productTypeFlags.groupedProduct ||
			product.productType == productTypeFlags.groupedProduct
		) {
			redirect(product.url || product.seName)

			return
		}

		if (
			product.productTypeId == productTypeFlags.simpleProduct ||
			product.productType == productTypeFlags.simpleProduct
		) {
			dispatch(requestAddItemToCart(product))
				.then(data => {
					const { crossSells, product, message } = data

					if (iterable(crossSells)) {
						// syncs with other modals
						window._drawerManager.open('added')

						// render with new data
						const addedToCartModal = document.getElementById('modal-added')

						// need a *new* key every time,
						// or if you add an item, and add cross-sells,
						// opening that same item again will show cross-sells as added
						const key = `added-${keygen()}`

						render(
							<AddedToCart
								productName={product.name}
								productQuantity={product.quantity}
								key={key}
								crossSells={crossSells}
								open
							/>,
							addedToCartModal,
						)
					} else {
						Toaster.success(message)
					}
				})
				.catch(error => {
					Toaster.error(error.message)
				})
		}
	}

	function onAddToWishlistClick(product) {
		dispatch(requestAddItemToWishlist(product))
			.then(d => {
				Toaster.success(d.message)
			})
			.catch(err => {
				Toaster.error(err.message)
			})
	}

	function onAddToCompareClick(product) {
		dispatch(requestAddItemToCompareList(product))
			.then(d => {
				Toaster.success(d.message)
			})
			.catch(err => {
				Toaster.error(err.message)
			})
	}

	return (
		<div className={`featured-list-container ${className}`}>
			{title && title}

			<section className="product-list">
				{products.map(item => {
					const productTypeFlag = item.productType || item.productTypeId
					const groupedProduct = productTypeFlag == productTypeFlags.groupedProduct
					const viewOptionsTitle = groupedProduct ? translate('product.viewoptions') : ''

					const addToCartTitle = item.productPrice.isRental
						? translate('shoppingcart.rent')
						: translate('shoppingcart.addtocart')

					const addToWishlistTitle = translate('shoppingcart.addtowishlist')

					return (
						<ProductBox
							key={item.id}
							product={item}
							eager={item.eager}
							addToWishlistTitle={addToWishlistTitle}
							addToCartTitle={viewOptionsTitle || addToCartTitle}
							addToComparelistTitle={translate('shoppingcart.addtocomparelist')}
							onAddToComparelistClick={
								!groupedProduct && compareProductsEnabled && onAddToCompareClick
							}
							onAddToWishlistClick={!groupedProduct && onAddToWishlistClick}
							onAddToCartClick={onAddToCartClick}
							showSku={settings.showSkuOnCatalogPages}
							displayShortDescription
						/>
					)
				})}
			</section>
		</div>
	)
}
